import { Injectable, inject } from '@angular/core';
import { NavigationEnd } from '@angular/router';

import { BulkLogService } from '../telemetry/bulk-log.service';
import { toTrimmedString } from '../../../shared/utils/coerce.utils';
import { SessionStorageService } from '../../storage/session-storage.service';
import { AuthStore } from '../../stores/auth.store';
import { ClassStore } from '../../stores/class.store';
import { UserStore } from '../../stores/user.store';

const LEGACY_VERB = {
  classStarted: 'CS',
  logout: 'LO',
  teacherPlanner: 'T-PTO',
  teacherLessons: 'T-LTO',
  teacherAssignments: 'T-ATO',
  teacherPerformance: 'T-PRTO',
  teacherLibrary: 'T-LBTO',
  teacherOrganizer: 'T-OTO',
  teacherProgress: 'T-PRGTO',
  studentDashboard: 'S-DTO',
  studentLibrary: 'S-LBTO',
  studentNotebook: 'S-NTO',
  studentAssignments: 'S-ATO',
  studentReview: 'S-RTO',
  studentConnect: 'S-CTO',
} as const;

/**
 * Session/route activity facade. The real SetBulkLog-compatible buffering
 * lives in BulkLogService so legacy WebAPI and the new xAPI backend stay in
 * lockstep.
 *
 * Timing mirrors the legacy Webclient (common.js): a screen's dwell time is
 * recorded as a single log entry when the user leaves that screen (or on
 * logout/teardown). This service never triggers a network send itself — it only
 * buffers entries via BulkLogService, which drains the buffer on its own ~60s
 * interval (legacy SETBULKLOGINTERVAL) and immediately on logout ("LO"). This
 * keeps SetBulkLog calls batched (~1/min) instead of firing on every route
 * change, and produces one dwell entry per screen visit rather than one per
 * minute.
 */
@Injectable({ providedIn: 'root' })
export class UsageLoggingService {
  private readonly bulkLog = inject(BulkLogService);
  private readonly auth = inject(AuthStore);
  private readonly classStore = inject(ClassStore);
  private readonly sessionStorage = inject(SessionStorageService);
  private readonly userStore = inject(UserStore);

  private currentRoute = '/';
  private currentStartedAt = 0;
  private currentVerbId = '';
  private started = false;
  private sessionSerial = 0;

  start(): void {
    // Attach BulkLogService's DOM/content listeners only once the session is
    // actually active (context ready) — don't capture content downloads before
    // login/class context exists, mirroring the legacy app.
    if (this.started || !this.contextReady()) return;
    this.bulkLog.start();
    this.sessionSerial++;
    this.started = true;
    this.currentStartedAt = Date.now();
    this.currentVerbId = this.verbIdForRoute(this.currentRoute);
    // Buffer the Class Started marker. BulkLogService starts its ~60s flush
    // timer off this first entry, matching the legacy interval — no immediate
    // network send here. VerbValue carries the login→class-start dwell when the
    // login flow recorded it (legacy trackCSLog), else 0.
    this.bulkLog.log(LEGACY_VERB.classStarted, {
      activityId: 'class-started',
      verbValue: toTrimmedString(this.sessionStorage.get('loginVerbVal')) || 0,
      objectName: 'Class Started',
    });
  }

  recordNavigation(event: NavigationEnd): void {
    const nextRoute = event.urlAfterRedirects || event.url || '/';
    if (!this.started) {
      this.currentRoute = nextRoute;
      this.start();
      return;
    }
    // Record the dwell for the screen being left as a single entry, then start
    // timing the new screen. Nothing is sent now — the buffer drains on the
    // BulkLogService interval.
    this.recordSegment();
    this.currentRoute = nextRoute;
    this.currentStartedAt = Date.now();
    this.currentVerbId = this.verbIdForRoute(this.currentRoute);
  }

  async stopAndFlush(reason: 'logout' | 'destroy' = 'destroy'): Promise<void> {
    const serial = this.sessionSerial;
    if (this.started) {
      // Capture the final screen's dwell before we tear down.
      this.recordSegment();
      // "LO" makes BulkLogService clear its timer and flush immediately.
      if (reason === 'logout') this.bulkLog.log(LEGACY_VERB.logout, { objectName: 'Logout' });
    }
    this.started = false;
    await this.bulkLog.flush();
    if (this.sessionSerial === serial) {
      this.currentRoute = '/';
      this.currentVerbId = '';
    }
  }

  /**
   * Buffers a single dwell-time entry for the current screen. Does NOT trigger a
   * network flush — sending is owned by BulkLogService's interval so calls stay
   * batched. Called on navigation and on teardown, never on a timer, so a screen
   * viewed for N minutes yields one entry (~N*60000ms), not N entries.
   */
  private recordSegment(now = Date.now()): void {
    if (!this.started || !this.contextReady()) return;
    const elapsedMs = Math.max(0, now - this.currentStartedAt);
    this.currentStartedAt = now;
    if (elapsedMs <= 0) return;
    const verbId = this.currentVerbId || this.verbIdForRoute(this.currentRoute);
    this.bulkLog.log(verbId, {
      activityId: this.currentRoute,
      objectName: this.routeName(this.currentRoute),
      verbValue: Math.round(elapsedMs),
      eventTimeStamp: now,
    });
  }

  private contextReady(): boolean {
    return Boolean(this.userStore.roleKnown() && this.userId() && this.classId());
  }

  private userId(): string {
    return this.auth.userId() || toTrimmedString(this.sessionStorage.get('userID'));
  }

  private classId(): string {
    return this.classStore.selectedClassId() || toTrimmedString(this.sessionStorage.get('classID'));
  }

  private verbIdForRoute(route: string): string {
    const path = route.split('?')[0].split('#')[0];
    const role = this.userStore.role();
    if (role === 'student') {
      if (path.startsWith('/library')) return LEGACY_VERB.studentLibrary;
      if (path.startsWith('/notebook')) return LEGACY_VERB.studentNotebook;
      if (path.startsWith('/assignments')) return LEGACY_VERB.studentAssignments;
      if (path.startsWith('/review')) return LEGACY_VERB.studentReview;
      if (path.startsWith('/connect')) return LEGACY_VERB.studentConnect;
      return LEGACY_VERB.studentDashboard;
    }
    if (path.startsWith('/planner')) return LEGACY_VERB.teacherPlanner;
    if (path.startsWith('/lessons')) return LEGACY_VERB.teacherLessons;
    if (path.startsWith('/assignments')) return LEGACY_VERB.teacherAssignments;
    if (path.startsWith('/performance')) return LEGACY_VERB.teacherPerformance;
    if (path.startsWith('/library')) return LEGACY_VERB.teacherLibrary;
    if (path.startsWith('/groups')) return LEGACY_VERB.teacherOrganizer;
    if (path.startsWith('/review')) return LEGACY_VERB.teacherLibrary;
    return LEGACY_VERB.teacherProgress;
  }

  private routeName(route: string): string {
    return route.replace(/^\/+/, '') || 'ClassView';
  }
}
